import { reviewAskCopy } from './review-ask-copy';
import { extUpdateCopy } from './ext-update-copy';
import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { dunningSelector, selectUserId } from '@common-reducers/UserSelectors';
import { useRouter } from '@common-reducers/hooks/use-router';
import {
  useGetNudgeQuery,
  useRecordNudgeEventMutation,
} from '@common-reducers/backend-api/backend-api';
import { dismissNudge, selectNudgeDismissed } from '@common-reducers/nudge-slice';
import { selectConnectedEnginesState } from '@common/reducers/ConnectedEnginesSlice';
import {
  isNudgeSatisfied,
  shouldRenderNudge,
  buildNudgeEventProps,
  resolveCtaUrl,
  isOnCtaTarget,
  isReviewAskRenderable,
  EXT_UPDATE_NUDGE_ID,
} from './nudge-banner-logic';
import type { NudgePayload, NudgeSurface } from './nudge-banner-logic';
import { isReviewAskNudgeId } from '@bt-common/nudges/nudge.types';
import type { NudgeClientEventName } from '@bt-common/nudges/nudge.types';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useReviewAskBlockers } from './review-ask-blockers';

const BANNER_HEIGHT = 40;
const STYLE_ID = 'bt-nudge-banner-style';

interface NudgeBannerProps {
  surface: NudgeSurface;
}

/**
 * Server-driven nudge banner (brand-blue), rendered on both the web app and the
 * WhatsApp Web extension. Dunning always wins: when the red dunning banner is
 * showing this renders nothing. Fail-closed — any fetch/render error => render nothing.
 */
const NudgeBanner: React.FC<NudgeBannerProps> = ({ surface }) => {
  const { isDunning } = useAppSelector(dunningSelector);
  const userId = useAppSelector(selectUserId);
  const { navigate, pathname } = useRouter();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(namespace);

  // Store-driven (mirrors DunningBannerExtension): the payload comes from the
  // CACHED RTK query, not a per-mount fetch. Skip until authed so the extension
  // doesn't fire a 401 at WhatsApp-Web page-load (before Blueticks login). On
  // re-injection remount the cached data is returned synchronously => the banner
  // renders instantly instead of restarting an async fetch that never resolves
  // before the next DOM-churn unmount.
  const { data } = useGetNudgeQuery(undefined, { skip: !userId });
  const [recordNudgeEvent] = useRecordNudgeEventMutation();

  const rawPayload = data?.nudge ?? null;
  const payload: NudgePayload | null =
    rawPayload && rawPayload.nudgeId && rawPayload.cta ? rawPayload : null;

  // Dismissal is store-backed (keyed by nudgeId) so it survives re-injection
  // remount; a local useState would reset to false and re-show the banner.
  const dismissed = useAppSelector((s) => selectNudgeDismissed(s, payload?.nudgeId));

  // #669: web only for now. The extension keeps its old behavior (server-side the nudge stops
  // being served once a Gateway has connected, so it drops there on the next fetch).
  const isGatewayConnected = useAppSelector((s) => selectConnectedEnginesState(s).isGatewayConnected);
  const satisfied = surface === 'web' && !!payload && isNudgeSatisfied(payload.nudgeId, isGatewayConnected);
  const onCtaTarget = surface === 'web' && !!payload && isOnCtaTarget(payload.cta.url, pathname);

  const shownRef = useRef(false);

  const dunningVisible = !!isDunning;
  // #787: review asks have their own placement + never render beside an open Crisp panel. Other nudges unaffected.
  const isReviewAsk = !!payload && isReviewAskNudgeId(payload.nudgeId);
  const reviewAskBlockers = useReviewAskBlockers(); // paywall / error / Crisp: re-hides if one opens after the ask is up
  const reviewAskOk = !isReviewAsk || isReviewAskRenderable(payload, 'banner', { blockers: reviewAskBlockers });
  const visible = !dismissed && !satisfied && !onCtaTarget && reviewAskOk && shouldRenderNudge(dunningVisible, payload, surface);

  // Fire nudge_shown once when the banner first renders.
  useEffect(() => {
    if (!visible || !payload || shownRef.current) return;
    shownRef.current = true;
    fireEvent('nudge_shown', payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Manage the extension global style that shifts #app down (mirrors DunningBannerExtension).
  useEffect(() => {
    if (surface !== 'extension') return;
    if (visible) {
      let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!style) {
        style = document.createElement('style');
        style.id = STYLE_ID;
        document.head.appendChild(style);
      }
      style.textContent = `
        #app {
          margin-top: ${BANNER_HEIGHT}px !important;
          height: calc(100vh - ${BANNER_HEIGHT}px) !important;
        }
      `;
    }
    return () => {
      const style = document.getElementById(STYLE_ID);
      if (style) style.remove();
    };
  }, [surface, visible]);

  const fireEvent = (name: NudgeClientEventName, p: NudgePayload) => {
    // Fire-and-forget: never block or break the banner on event failure.
    try {
      recordNudgeEvent({ name, props: buildNudgeEventProps(p, surface) })
        .catch(() => { /* intentionally ignored */ });
    } catch {
      // never break the banner
    }
  };

  if (!visible || !payload) return null;

  const handleCtaClick = () => {
    fireEvent('nudge_clicked', payload);
    try {
      if (isReviewAsk) {
        // Absolute Chrome Web Store URL on BOTH surfaces; a click is final (server records it), so also hide it locally.
        window.open(payload.cta.url, '_blank', 'noopener,noreferrer');
        dispatch(dismissNudge(payload.nudgeId));
      } else if (surface === 'web') {
        navigate(payload.cta.url);
      } else {
        const url = resolveCtaUrl(payload.cta.url, 'extension');
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // never break the banner
    }
  };

  const handleDismiss = () => {
    fireEvent('nudge_dismissed', payload);
    dispatch(dismissNudge(payload.nudgeId));
  };

  const isExtUpdate = String(payload.nudgeId) === EXT_UPDATE_NUDGE_ID;
  const extCopy = extUpdateCopy(t);
  const copy = isExtUpdate
    ? { headline: extCopy.headline, body: '', cta: extCopy.cta }
    : isReviewAsk && isReviewAskNudgeId(payload.nudgeId)
    ? {
        ...reviewAskCopy(t, payload.nudgeId, payload),
        cta: t('reviewAsk.write', { defaultValue: payload.cta.label }),
      }
    : { headline: payload.headline, body: payload.body, cta: payload.cta.label };

  const containerStyle: React.CSSProperties =
    surface === 'extension'
      ? {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: `${BANNER_HEIGHT}px`,
          zIndex: 10000,
          boxSizing: 'border-box',
        }
      : { width: '100%' };

  // Calm service-message look for the update nudge: soft blue surface, dark text, solid blue action.
  const surfaceBg = isExtUpdate ? '#eff6ff' : '#2563eb';
  const surfaceFg = isExtUpdate ? '#1e3a8a' : '#fff';
  const ctaBg = isExtUpdate ? '#2563eb' : '#fff';
  const ctaFg = isExtUpdate ? '#fff' : '#2563eb';

  return (
    <div dir="auto" role="status" style={{
      ...containerStyle,
      backgroundColor: surfaceBg,
      color: surfaceFg,
      borderBottom: isExtUpdate ? '1px solid #bfdbfe' : undefined,
      padding: surface === 'extension' ? '0 16px' : '10px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      fontSize: surface === 'extension' ? '13px' : '14px',
      fontWeight: 500,
    }}>
      <span>
        <strong>{copy.headline}</strong>
        {copy.body && <span style={{ marginInlineStart: 8, fontWeight: 400 }}>{copy.body}</span>}
      </span>
      <button
        onClick={handleCtaClick}
        style={{
          backgroundColor: ctaBg,
          color: ctaFg,
          border: 'none',
          borderRadius: isExtUpdate ? '999px' : '4px',
          padding: surface === 'extension' ? '4px 12px' : '6px 16px',
          fontWeight: 600,
          cursor: 'pointer',
          fontSize: surface === 'extension' ? '12px' : '13px',
          whiteSpace: 'nowrap',
        }}
      >
        {copy.cta}
      </button>
      {isReviewAsk && (
        <button
          onClick={handleDismiss}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.6)',
            borderRadius: '4px',
            color: '#fff',
            cursor: 'pointer',
            padding: surface === 'extension' ? '3px 10px' : '5px 14px',
            fontSize: surface === 'extension' ? '12px' : '13px',
            whiteSpace: 'nowrap',
          }}
        >
          {t('reviewAsk.notNow', { defaultValue: 'Not now' })}
        </button>
      )}
      <button
        aria-label={isExtUpdate ? extCopy.dismiss : 'Dismiss'}
        onClick={handleDismiss}
        style={{
          background: 'transparent',
          border: 'none',
          color: surfaceFg,
          cursor: 'pointer',
          fontSize: '18px',
          lineHeight: 1,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
};

export default NudgeBanner;
